import { formatUnitAwareNumber } from '@monorepo/shared/utils/formatUnitAwareNumber';
import { format } from 'date-fns';
import {
  FieldDataType,
  localEquivalentOfUTC,
  SpecificFieldValue,
} from 'mapistry-shared';
import React from 'react';
import { ReactAutolinker } from '../../ReactAutolinker';
import { fieldValueFactory } from './fieldValueFactory';

interface FieldValueDisplayFactoryParams {
  fieldType: FieldDataType;
  fieldValue: SpecificFieldValue;
}

export const fieldValueDisplayFactory = ({
  fieldType,
  fieldValue,
}: FieldValueDisplayFactoryParams) => {
  const defaultResult = '';
  if (fieldValue.value == null) {
    return defaultResult;
  }
  switch (fieldType) {
    case FieldDataType.BOOLEAN: {
      const { value } = fieldValueFactory({ fieldValue, fieldType }) || {};
      return value ? 'Yes' : 'No';
    }
    case FieldDataType.DATE: {
      const { value } = fieldValueFactory({ fieldValue, fieldType }) || {};
      return value
        ? format(localEquivalentOfUTC(new Date(value)), 'P')
        : defaultResult;
    }
    case FieldDataType.DATETIME: {
      const { value } = fieldValueFactory({ fieldValue, fieldType }) || {};
      return value
        ? format(localEquivalentOfUTC(new Date(value)), 'Pp')
        : defaultResult;
    }
    case FieldDataType.NUMERIC: {
      const { givenUnits, value } =
        fieldValueFactory({ fieldValue, fieldType }) || {};
      if (value == null) {
        return defaultResult;
      }
      return formatUnitAwareNumber(value, givenUnits);
    }
    case FieldDataType.RESOURCE: {
      const { value } = fieldValueFactory({ fieldValue, fieldType }) || {};
      return value ? value.name : defaultResult;
    }
    case FieldDataType.SINGLE_SELECT: {
      const { value } = fieldValueFactory({ fieldValue, fieldType }) || {};
      return value || defaultResult;
    }
    case FieldDataType.TEXT: {
      const { value } = fieldValueFactory({ fieldValue, fieldType }) || {};
      return <ReactAutolinker textToLink={value || defaultResult} />;
    }
    case FieldDataType.TIME: {
      const { value } = fieldValueFactory({ fieldValue, fieldType }) || {};
      return value || defaultResult;
    }
    case FieldDataType.USER: {
      const { value } = fieldValueFactory({ fieldValue, fieldType }) || {};
      return value?.name ?? defaultResult;
    }
    case FieldDataType.FORMULA:
    default: {
      const exhaustiveCheck: never | FieldDataType.FORMULA = fieldType;
      throw new Error(`Unhandled field data type - ${exhaustiveCheck}`);
    }
  }
};
